import React from 'react'
import Link from '../components/Link'
import {
  AcademicCapIcon,
  HeartIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline'

const About: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <main className="flex-1 overflow-auto min-h-0">
        <section className="py-12 px-4 md:p-12 max-w-4xl mx-auto">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-6 text-center">
            About TrackIEP
          </h1>
          <p className="text-lg text-gray-700 mb-4">
            TrackIEP was created with a simple belief: every educator deserves
            the tools to deliver IEP accommodations and track goals with
            transparency, accountability, and care.
          </p>
          <p className="text-lg text-gray-700 mb-4">
            We understand the challenges that educators face in managing student
            accommodations and goals. The spreadsheets, the scattered
            documentation, the difficulty in tracking compliance—these are real
            obstacles that take time away from what matters most: supporting
            students.
          </p>
          <p className="text-lg text-gray-700 mb-8">
            TrackIEP provides a centralized platform that empowers educators to
            deliver, monitor, and report student accommodations and goals with
            ease. Our mission is to streamline the accommodation and goal
            tracking process so that educators can focus on what they do
            best—teaching and supporting their students.
          </p>
        </section>

        <section className="bg-blue-100 py-12 px-4 md:p-12">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Our Core Values
            </h2>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="card flex-1 bg-white rounded-lg shadow-md p-6">
                <AcademicCapIcon className="w-12 h-12 text-blue-600 mb-4 mx-auto" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  Educator-Focused
                </h3>
                <p className="text-gray-600 text-center">
                  We design our tools with educators in mind, ensuring that
                  every feature serves to simplify and support their daily work.
                </p>
              </div>
              <div className="card flex-1 bg-white rounded-lg shadow-md p-6">
                <HeartIcon className="w-12 h-12 text-blue-600 mb-4 mx-auto" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  Student-Centered
                </h3>
                <p className="text-gray-600 text-center">
                  At the heart of everything we do is the goal of improving
                  outcomes for students who rely on accommodations and goal
                  tracking to succeed.
                </p>
              </div>
              <div className="card flex-1 bg-white rounded-lg shadow-md p-6">
                <LightBulbIcon className="w-12 h-12 text-blue-600 mb-4 mx-auto" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  Innovation & Simplicity
                </h3>
                <p className="text-gray-600 text-center">
                  We believe technology should make life easier, not more
                  complicated. We strive to create intuitive, powerful
                  solutions.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 px-4 md:p-12 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Why TrackIEP?
          </h2>
          <div className="space-y-6 text-gray-700">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Built by Educators, for Educators
              </h3>
              <p>
                Our team includes experienced educators who understand the
                day-to-day realities of managing IEP accommodations and goals.
                We've lived the challenges and built the solution we wish we'd
                had.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Commitment to Security & Privacy
              </h3>
              <p>
                Student data is sacred. We implement industry-leading security
                measures and comply with all relevant privacy regulations to
                ensure that sensitive information remains protected.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Continuous Improvement
              </h3>
              <p>
                We're constantly listening to feedback from educators and
                refining TrackIEP to better meet the evolving needs of schools
                and students. Your input shapes our development roadmap.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-blue-500 py-12 px-4 md:p-12 flex flex-col gap-8 justify-center items-center">
          <h2 className="text-3xl text-center font-semibold text-white mb-3 max-w-4xl">
            Ready to see how TrackIEP can transform accommodation and goal
            management at your school?
          </h2>
          <Link
            variant="secondary"
            to="/request-demo"
            className="group md:max-w-xs md:mx-auto relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
          >
            Request a Demo
          </Link>
        </section>
      </main>
    </div>
  )
}

export default About
